import { useCallback } from 'react';

import {
  DEFAULT_PROMPT_COOLDOWN_HOURS,
  PromptStorageScope,
  isPromptCooldownElapsed,
  writePromptLastShownAt,
} from '../helpers/prompt-scheduler.helpers';

export interface IUsePromptSchedulerArgs {
  // Stable per-prompt-type key — each feature gets its own independent cooldown.
  promptKey: string;
  // Per-user prompts: pass the user id — it scopes the storage key, and while it is still
  // undefined (identity loading) the scheduler stays inert. Omit for device-wide prompts.
  userId?: string;
  // Min hours between showings (defaults to the global promptCooldownHours).
  cooldownHours?: number;
  // Caller-composed suppression: while true the prompt may not fire (e.g. mid-evaluation).
  // Callers OR their own conditions into this one flag — that's how they "register" them.
  suppressed?: boolean;
  // 'persistent' (default, localStorage) or 'session' (sessionStorage, per browser session).
  scope?: PromptStorageScope;
}

export interface IUsePromptSchedulerReturn {
  // True iff the cooldown has elapsed AND nothing is suppressing the prompt right now.
  canShow: boolean;
  // Call when the prompt is actually shown — starts a fresh cooldown.
  markShown: () => void;
  // Call on dismiss / "remind me later" — resets the cooldown from now, same as a showing.
  snooze: () => void;
}

// HOOK: throttled-prompt gate for one prompt-type. Generic — each feature passes its own
// `promptKey`, plus `userId` when the cooldown is per-user; nothing here is calibration-aware.
export const usePromptScheduler = (args: IUsePromptSchedulerArgs): IUsePromptSchedulerReturn => {
  const {
    promptKey,
    userId,
    cooldownHours = DEFAULT_PROMPT_COOLDOWN_HOURS,
    suppressed = false,
    scope = 'persistent',
  } = args;

  // Passing the `userId` key at all (even undefined, like a controlled prop) opts into per-user
  // scoping; an unresolved id keeps the scheduler inert so ":undefined" never reaches storage.
  const userScoped = 'userId' in args;
  const identityPending = userScoped && !userId;
  const effectiveKey = userScoped && userId ? `${promptKey}:${userId}` : promptKey;

  const markShown = useCallback(() => {
    if (identityPending) return;
    writePromptLastShownAt(effectiveKey, Date.now(), scope);
  }, [effectiveKey, identityPending, scope]);

  // Recomputed each render (reads storage + clock, both non-reactive); memoizing
  // would freeze a cooldown that elapses between the consumer's renders.
  const canShow = !suppressed && !identityPending
    && isPromptCooldownElapsed(effectiveKey, cooldownHours, Date.now(), scope);

  return { canShow, markShown, snooze: markShown };
};
